/**
 * Tab-scoped AVT slider persistence.
 * Survives TuningModal unmount. Fresh tabs start at schema defaults.
 *
 * Semantics:
 * DEFAULT VALUE — TUNING_VARS[name].default (matches sketch.js globals)
 * CURRENT / LAST APPLIED — sliders live-apply via APPLY_TUNING; this map is both
 * CURRENT EFFECTIVE — avatar globals after live apply, or sketch.js defaults
 *                      after a cold iframe load until stored values are re-posted
 */

export const TUNING_STORAGE_KEY = 'voiceBotTuningValues';

export function getTuningDefaults(schema) {
  return Object.fromEntries(
    Object.entries(schema || {}).map(([name, spec]) => [name, spec.default])
  );
}

export function readStoredTuningPayload() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(TUNING_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function readTuningValues(defaults) {
  const base = { ...(defaults || {}) };
  const stored = readStoredTuningPayload();
  if (!stored) return base;
  return { ...base, ...stored };
}

export function readTuningNumber(name, fallback) {
  const stored = readStoredTuningPayload();
  const v = Number(stored?.[name]);
  return Number.isFinite(v) ? v : fallback;
}

export function writeTuningValues(values) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(TUNING_STORAGE_KEY, JSON.stringify(values || {}));
  } catch {
    /* optional */
  }
}

export function clearTuningValues() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(TUNING_STORAGE_KEY);
  } catch {
    /* optional */
  }
}

export const TUNING_POST_ORIGIN = 'https://voicebot.ispeakwell.ca';

/** Post AVT slider payload to every VoiceBot runtime that shares the studio session. */
export function postTuningPayload(payload, origin = TUNING_POST_ORIGIN) {
  if (!payload || typeof payload !== 'object' || typeof window === 'undefined') return false;
  const ids = ['theBot', 'theBotTools'];
  let sent = false;
  for (let i = 0; i < ids.length; i += 1) {
    const win = document.getElementById(ids[i])?.contentWindow;
    if (!win) continue;
    try {
      win.postMessage({ type: 'APPLY_TUNING', payload }, origin);
      sent = true;
    } catch {
      /* iframe may not be ready */
    }
  }
  return sent;
}
